import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import { QuestionTypeDropdown } from "components/CustomDropdown";
import api from "utils/api";
import { toast } from "react-toastify";

const EditQuestionDialog = ({ open, onClose, onSuccess, question }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    question: "",
    subject: "",
    questionType: "VERBAL",
  });
  const [errors, setErrors] = useState({});

  // Pre-fill form when question prop changes
  useEffect(() => {
    if (question && open) {
      setFormData({
        question: question.question || "",
        subject: question.subject || "",
        questionType: question.questionType || "VERBAL",
      });
      setErrors({});
    }
  }, [question, open]);

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.question || formData.question.trim().length < 5) {
      newErrors.question = "Question must be at least 5 characters long";
    }
    if (formData.question && formData.question.trim().length > 500) {
      newErrors.question = "Question cannot exceed 500 characters";
    }
    if (!formData.subject || formData.subject.trim().length < 2) {
      newErrors.subject = "Subject must be at least 2 characters long";
    }
    if (formData.subject && formData.subject.trim().length > 100) {
      newErrors.subject = "Subject cannot exceed 100 characters";
    }
    if (!formData.questionType) {
      newErrors.questionType = "Question type is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async () => {
    if (!validateForm() || !question?._id) return;

    setLoading(true);
    try {
      const response = await api.put(`/questions/${question._id}`, {
        question: formData.question.trim(),
        subject: formData.subject.trim(),
        questionType: formData.questionType,
      });

      if (response.data.success) {
        toast.success("Question updated successfully!");
        handleClose();
        onSuccess && onSuccess();
      }
    } catch (error) {
      console.error("Error updating question:", error);
      toast.error(error.response?.data?.message || "Failed to update question");
    } finally {
      setLoading(false);
    }
  };

  // Handle close
  const handleClose = () => {
    setFormData({ question: "", subject: "", questionType: "VERBAL" });
    setErrors({});
    onClose();
  };

  // Handle input change
  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  return (
    <Dialog open={open} onClose={!loading ? handleClose : undefined} maxWidth="md" fullWidth>
      <DialogTitle>
        <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
          <ArgonBox>
            <ArgonTypography variant="h5" fontWeight="medium" color="primary">
              ✏️ Edit Question
            </ArgonTypography>
            <ArgonTypography variant="caption" color="secondary">
              Update the question details
            </ArgonTypography>
          </ArgonBox>
          {!loading && (
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          )}
        </ArgonBox>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3} sx={{ mt: 0.5 }}>
          {/* Question Field */}
          <Grid item xs={12}>
            <ArgonTypography variant="subtitle2" fontWeight="medium" color="text" mb={1}>
              Question <span style={{ color: "red" }}>*</span>
            </ArgonTypography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Enter your question here..."
              value={formData.question}
              onChange={(e) => handleChange("question", e.target.value)}
              error={!!errors.question}
              helperText={errors.question || `${formData.question.length}/500 characters`}
              disabled={loading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "rgba(0,0,0,0.02)",
                },
              }}
            />
          </Grid>

          {/* Subject Field */}
          <Grid item xs={12} md={6}>
            <ArgonTypography variant="subtitle2" fontWeight="medium" color="text" mb={1}>
              Subject <span style={{ color: "red" }}>*</span>
            </ArgonTypography>
            <TextField
              fullWidth
              placeholder="e.g., Mathematics, Science, English..."
              value={formData.subject}
              onChange={(e) => handleChange("subject", e.target.value)}
              error={!!errors.subject}
              helperText={errors.subject}
              disabled={loading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "rgba(0,0,0,0.02)",
                },
              }}
            />
          </Grid>

          {/* Question Type Field */}
          <Grid item xs={12} md={6}>
            <ArgonTypography variant="subtitle2" fontWeight="medium" color="text" mb={1}>
              Question Type <span style={{ color: "red" }}>*</span>
            </ArgonTypography>
            <QuestionTypeDropdown
              value={formData.questionType}
              onChange={(value) => handleChange("questionType", value)}
              width="100%"
              size="medium"
              showIcons={true}
            />
            {errors.questionType && (
              <ArgonTypography variant="caption" color="error" mt={0.5}>
                {errors.questionType}
              </ArgonTypography>
            )}
          </Grid>

          {/* Show original data for reference */}
          {question && (
            <Grid item xs={12}>
              <ArgonBox
                p={2}
                backgroundColor="rgba(0,0,0,0.05)"
                borderRadius={2}
                border="1px solid rgba(0,0,0,0.1)"
              >
                <ArgonTypography variant="caption" color="secondary" mb={1}>
                  Original Question:
                </ArgonTypography>
                <ArgonTypography variant="body2" color="text">
                  {question.question}
                </ArgonTypography>
              </ArgonBox>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <ArgonButton variant="outlined" onClick={handleClose} disabled={loading}>
          Cancel
        </ArgonButton>
        <ArgonButton
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loading || !formData.question.trim() || !formData.subject.trim()}
          sx={{ minWidth: 120 }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Update Question"}
        </ArgonButton>
      </DialogActions>
    </Dialog>
  );
};

EditQuestionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  question: PropTypes.shape({
    _id: PropTypes.string,
    question: PropTypes.string,
    subject: PropTypes.string,
    questionType: PropTypes.oneOf(["VERBAL", "WRITTEN"]),
  }),
};

EditQuestionDialog.defaultProps = {
  onSuccess: null,
  question: null,
};

export default EditQuestionDialog;
